import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Eye, EyeOff, Loader2, Mail, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';

const LOGO_URL =
  'https://customer-assets.emergentagent.com/job_5935cf64-512e-4adb-972b-b8d08d999032/artifacts/9m3z1jc8_thumbnail_PHOTO-2025-02-05-22-08-51.jpg';

function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : '')).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return 'Something went wrong. Please try again.';
}

const Signup = () => {
  const navigate = useNavigate();
  const { register, loginWithGoogle } = useAuth();

  // Honor ?role=landlord in the URL (linked from landing page footer / CTAs).
  const initialRole = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const r = (params.get('role') || '').toLowerCase();
      return r === 'landlord' ? 'landlord' : 'student';
    } catch (_) {
      return 'student';
    }
  })();

  const [showEmailForm, setShowEmailForm] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isStudent, setIsStudent] = useState(true);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [role] = useState(initialRole);

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) return setError("What's your name? We need that to set things up.");
    if (!isEmailValid) return setError("Hmm, that email doesn’t look right. Can you check it?");
    if (password.length < 8)
      return setError('Your password needs to be at least 8 characters.');
    // Landlords self-describe as such (via ?role=landlord). Students must
    // tick the "I'm a student / renter" box.
    if (role === 'student' && !isStudent)
      return setError('Sturent is currently only for students and working professionals looking for housing.');
    if (!agreed)
      return setError('You need to agree to the Terms of Service and Privacy Policy to continue.');

    setSubmitting(true);
    try {
      await register({ name: name.trim(), email: email.trim().toLowerCase(), password, role });
      const params = new URLSearchParams(window.location.search);
      const nextParam = params.get('next');
      // Landlords don't run the student onboarding flow — they go straight to
      // /home (which will show a "List your first place" CTA via the
      // role-aware dashboard).
      const defaultTarget = role === 'landlord' ? '/home' : '/welcome';
      const target = nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//')
        ? nextParam
        : defaultTarget;
      navigate(target);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="signup-page">
      <header className="px-5 py-4">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900" data-testid="back-to-home">
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm font-medium">Back</span>
        </Link>
      </header>

      <main className="flex-1 px-5 pb-10">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="Sturent" className="h-12 w-12 rounded-2xl object-cover shadow-sm mx-auto mb-5" />
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold text-slate-900">
              {role === 'landlord' ? 'List your GTA property with Sturent.' : 'Create your free Sturent account.'}
            </h1>
            <p className="mt-2 text-sm sm:text-base text-slate-600">
              {role === 'landlord'
                ? 'We verify every landlord before a listing goes live. It takes about 10 minutes to post your first place.'
                : 'Save listings, message landlords, and get alerts when new places open up near your school.'}
            </p>
            {role === 'landlord' && (
              <p className="mt-2 inline-block text-[11px] font-semibold uppercase tracking-wider text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full" data-testid="landlord-role-badge">
                Signing up as a landlord
              </p>
            )}
          </div>

          {error && (
            <Alert className="mb-4 border-rose-200 bg-rose-50" data-testid="signup-error">
              <AlertDescription className="text-rose-800 text-sm">{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-3">
            <Button
              type="button"
              onClick={loginWithGoogle}
              className="w-full h-12 btn-primary text-base"
              data-testid="signup-google-btn"
            >
              <GoogleGlyph />
              Continue with Google
            </Button>

            {!showEmailForm && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEmailForm(true)}
                className="w-full h-12 text-base bg-white border-slate-200 text-slate-900 hover:bg-slate-50"
                data-testid="signup-email-toggle"
              >
                <Mail className="mr-2 h-4 w-4" />
                Sign up with email
              </Button>
            )}
          </div>

          {showEmailForm && (
            <motion.form
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              onSubmit={handleSubmit}
              noValidate
              className="mt-5 space-y-4 bg-white rounded-2xl border border-slate-200 p-5 shadow-soft"
              data-testid="signup-email-form"
            >
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-slate-800">Full name</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                  className="mt-1.5 h-11"
                  data-testid="signup-name-input"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-sm font-medium text-slate-800">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  className="mt-1.5 h-11"
                  data-testid="signup-email-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-sm font-medium text-slate-800">
                  Password <span className="text-slate-400 font-normal">(at least 8 characters)</span>
                </Label>
                <div className="relative mt-1.5">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    className="h-11 pr-11"
                    data-testid="signup-password-input"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {role === 'student' && (
                <div className="flex items-start gap-2.5">
                  <Checkbox
                    id="role"
                    checked={isStudent}
                    onCheckedChange={(v) => setIsStudent(!!v)}
                    className="mt-0.5"
                    data-testid="signup-role-checkbox"
                  />
                  <label htmlFor="role" className="text-sm text-slate-700 leading-relaxed cursor-pointer">
                    I’m a student or working professional looking for housing.
                  </label>
                </div>
              )}
              {role === 'landlord' && (
                <div className="px-3 py-2 bg-amber-50 border border-amber-200/60 rounded-lg text-xs text-amber-900 leading-relaxed" data-testid="landlord-verify-note">
                  Heads up: before your first listing goes live, we'll verify your identity
                  and proof of ownership. (Identity verification is a Phase 2 feature — for now
                  you can create and submit listings for review.)
                </div>
              )}

              <div className="flex items-start gap-2.5">
                <Checkbox
                  id="agree"
                  checked={agreed}
                  onCheckedChange={(v) => setAgreed(!!v)}
                  className="mt-0.5"
                  data-testid="signup-terms-checkbox"
                />
                <label htmlFor="agree" className="text-sm text-slate-700 leading-relaxed cursor-pointer">
                  I agree to the{' '}
                  <Link to="/terms" className="underline text-slate-900 font-medium">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="underline text-slate-900 font-medium">Privacy Policy</Link>.
                </label>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 btn-primary text-base"
                data-testid="signup-submit-btn"
              >
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create account
              </Button>
            </motion.form>
          )}

          <p className="text-center text-sm text-slate-600 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-slate-900 underline" data-testid="signup-to-login">
              Sign in
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
};

const GoogleGlyph = () => (
  <svg className="mr-2 h-4 w-4" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"/>
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
  </svg>
);

export default Signup;
